import { fairSpread, gainOverBook } from './edge.js';

// Compare every book against the frozen pool line from one team's perspective.
//
// Two numbers per book, deliberately. `points` is the posted comparison — the
// spread the book displays, which is what a human is looking at on the card.
// `gain` is the probability the pool line holds over that book's *fair* number,
// de-vigged from both prices: a book showing -4 at -105 is not offering the same
// bet as one showing -4 at -120, and half a point of price skew near a key
// number is worth more than the posted numbers admit. Ranking and shading read
// `gain`; the displayed line stays posted, because a book shows what it posts.
//
// With prices missing or symmetric (the seeded development data), `fairSpread`
// returns the posted number and the two agree.
//
// Ranking and display are two different perspectives, and they are separated
// on purpose. `side` is the side the *edge* is on — everything that answers
// "is this good for us" (`points`, `fairPoints`, `gain`, and therefore the
// order and the colour) is signed from it. `displaySide` only decides which
// way round the printed numbers read. The panel ranks from the edge side and
// prints from the home side, so its axis runs the same direction as the card's
// tug bar above it; passing one argument keeps the old behaviour of both.

// Probabilities are percentages here, so a thousandth of a point is far below
// anything a person could see.
//
// It was 1e-6, sized against the 1e-13 wobble of one shared summation. Each
// book now carries its own calibrated distribution, so two books whose numbers
// win on exactly the same margins land a few parts in 1e-5 apart rather than a
// few in 1e-13 — real arithmetic, but shape noise rather than information, and
// a tier of genuinely equivalent books was sorting on it. The epsilon exists
// precisely to stop that, so it is sized to the new construction.
const GAIN_EPSILON = 1e-3;

// The per-book gain lives in `edge.js` as `gainOverBook`, so this panel and the
// "N for / N against" counts above it cannot drift apart.
//
// Each row is now scored under ITS OWN book's calibrated distribution, solved
// from that book's posted spread and price pair, and the gain is the outright
// win probability our frozen line holds over the number that book is actually
// offering. Both terms of the difference use the same distribution, so the
// model's calibration error largely cancels.
//
// That is a reversal of what this comment used to say, and the reason is worth
// keeping. Scoring every row under one consensus-derived distribution was done
// to stop the gain going non-monotonic in the fair number. It did that, but it
// tied every per-book number to a synthetic continuous spread — and a weighted
// mean of books all posting the same integer lands a hair off it, which was
// enough to hand a frozen integer half the mass on a key number as free value.
// Monotonicity under the per-book construction was measured rather than
// assumed: see the sweep in `edge.spec.js`, 4,312 checks, no failures.
//
// Rows carry two comparisons, because the panel and the card answer different
// questions and each has to be coherent on its own terms.
//
//   `favour` — points of de-vigged line this book sits past ours, signed to
//              our side. Continuous, so it places a dot and shades it with one
//              number, and it is what the "N favour" count reads. DESCRIPTIVE:
//              where the market really is.
//   `gain`   — outright win probability our frozen line holds over the number
//              this book POSTS. Quantised to whole margins, because that is
//              what a margin is. This is the recommendation's currency and the
//              card bands on its mixture.
//
// Conflating them is what went wrong twice: shading `favour`-placed dots with
// `gain` put position and colour on different quantities, and placing dots by
// posted numbers to match `gain` collapsed nine books into four columns and
// hid the price information the de-vig exists to recover.

export function rankBooks(game, side = 'home', displaySide = side) {
  const direction = side === 'away' ? -1 : 1;
  const shown = displaySide === 'away' ? -1 : 1;
  const frozen = Number.isFinite(game.fivePicksSpread) ? game.fivePicksSpread : null;
  return (game.spreads || []).map(book => {
    const available = Number.isFinite(book.homeSpread);
    const points = available && frozen != null
      ? Math.round((frozen - book.homeSpread) * direction * 1000000) / 1000000
      : null;
    const fair = available ? fairSpread(book) : null;
    const fairPoints = fair == null || frozen == null
      ? null
      : Math.round((frozen - fair) * direction * 1000000) / 1000000;
    const gain = frozen == null ? null : gainOverBook(book, frozen, direction);
    return {
      // Points of de-vigged line past ours — see the note above.
      favour: fairPoints,
      book,
      line: available ? book.homeSpread * shown : null,
      fairLine: fair == null ? null : fair * shown,
      price: displaySide === 'away' ? book.awayPrice : book.homePrice,
      // The de-vig reads both prices, so a row that shows only one cannot be
      // checked: two books posting -2.5 with this side at -108 land on
      // different fair numbers when their other sides are -112 and -104.
      oppositePrice: displaySide === 'away' ? book.homePrice : book.awayPrice,
      points,
      fairPoints,
      gain,
    };
  }).sort((a, b) => {
    if (a.line == null && b.line != null) return 1;
    if (b.line == null && a.line != null) return -1;
    if (a.favour != null && b.favour != null) {
      // Compare the probabilities at the resolution they actually carry. NFL
      // margins are integers, so a frozen -3 against a fair -3.32 and against a
      // fair -3.74 win on exactly the same margin — the two gains are equal to
      // twelve decimal places and differ only in float noise. Subtracting them
      // raw sorted a whole tier of books on that noise, which reads as no sort
      // at all. Below the epsilon, order by how much number we hold instead.
      const difference =
        Math.abs(b.favour - a.favour) > GAIN_EPSILON ? b.favour - a.favour : 0;
      const ordered = difference || b.points - a.points;
      if (ordered) return ordered;
    }
    return String(a.book.bookmaker || '').localeCompare(String(b.book.bookmaker || ''));
  });
}
